import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Seo from '@/components/Seo';
import BookingForm from '@/components/BookingForm';
import Footer from '@/components/Footer';
import { landingPages } from '@/data/landingPages';

const PHONE = '+33674626368';
const PHONE_DISPLAY = '+33 6 74 62 63 68';
const WHATSAPP_URL = 'https://wa.me/33674626368?text=Bonjour%2C%20je%20souhaite%20r%C3%A9server%20un%20taxi%20Yellow%20Cab%20Corsica.';
const BASE_URL = 'https://taxiyellowcabcorsica.fr';

const buildJsonLd = (page) => [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": page.breadcrumb, "item": `${BASE_URL}/${page.slug}` }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": page.breadcrumb,
    "name": page.breadcrumb,
    "description": page.metaDescription,
    "url": `${BASE_URL}/${page.slug}`,
    "areaServed": ["Porticcio", "Ajaccio", "Corse-du-Sud"],
    "provider": {
      "@type": "LocalBusiness",
      "name": "Taxis Yellow Cab Corsica",
      "telephone": "+33674626368",
      "url": BASE_URL
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": page.faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  }
];

const LandingPage = ({ slug }) => {
  const { t } = useTranslation();
  const page = landingPages[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const related = page.related.map((s) => landingPages[s]);

  return (
    <main className="bg-[#0A0A0A]" data-testid={`landing-${page.slug}`}>
      <Seo
        title={page.title}
        description={page.metaDescription}
        canonical={`${BASE_URL}/${page.slug}`}
        jsonLd={buildJsonLd(page)}
      />

      {/* Hero */}
      <section className="relative pt-14 pb-12 px-4 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <nav className="flex items-center gap-1.5 text-xs text-[#555] mb-8" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#A3A3A3] transition-colors">Accueil</Link>
            <ChevronRight size={12} />
            <span className="text-[#A3A3A3]">{page.breadcrumb}</span>
          </nav>
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#EAB308] font-semibold border border-[#EAB308]/25 bg-[#EAB308]/5 rounded-full px-4 py-1.5 mb-6">
            {page.badge}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {page.h1_1}{' '}
            <span className="text-[#EAB308]">{page.h1_2}</span>
          </h1>
          <p className="text-[#A3A3A3] text-base md:text-lg leading-relaxed mb-8 max-w-2xl">{page.subtitle}</p>
          <div className="flex flex-wrap gap-3">
            <a href={`tel:${PHONE}`} data-testid="landing-call-btn"
              className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold px-6 py-3.5 rounded-full text-sm transition-colors">
              <Phone size={16} /> {PHONE_DISPLAY}
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-6 py-3.5 rounded-full text-sm transition-colors">
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Intro + content */}
      <section className="py-14 px-4">
        <div className="max-w-4xl mx-auto space-y-5">
          {page.intro.map((p, i) => (
            <p key={i} className="text-[#A3A3A3] text-base leading-relaxed">{p}</p>
          ))}
        </div>
      </section>

      {/* Highlights */}
      <section className="px-4 pb-14">
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4">
          {page.highlights.map((h) => (
            <div key={h.title} className="bg-[#1A1A1A] border border-white/5 rounded-2xl p-6">
              <CheckCircle2 size={20} className="text-[#EAB308] mb-3" />
              <h3 className="text-white font-semibold text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{h.title}</h3>
              <p className="text-[#A3A3A3] text-sm leading-relaxed">{h.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sections */}
      {page.sections.map((s) => (
        <section key={s.h2} className="px-4 pb-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-light text-white tracking-tight mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {s.h2}
            </h2>
            <div className="space-y-4">
              {s.paragraphs.map((p, i) => (
                <p key={i} className="text-[#A3A3A3] text-base leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Booking form */}
      <section id="reservation" className="bg-[#0D0D0D] border-y border-white/5 py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#EAB308] font-semibold mb-3">{t('booking.label')}</p>
            <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('booking.title')}
            </h2>
            <p className="text-[#A3A3A3] mt-3 text-base">{t('booking.subtitle')}</p>
          </div>
          <BookingForm />
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-light text-white tracking-tight mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Questions fréquentes
          </h2>
          <div className="space-y-3">
            {page.faq.map((f) => (
              <details key={f.q} className="bg-[#1A1A1A] border border-white/8 rounded-xl px-5 py-4 group">
                <summary className="text-white text-sm font-medium cursor-pointer list-none flex items-center justify-between gap-4">
                  {f.q}
                  <ChevronRight size={15} className="text-[#EAB308] shrink-0 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-[#A3A3A3] text-sm leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related pages */}
      <section className="px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-semibold text-white mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Nos autres services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} data-testid={`related-${r.slug}`}
                className="bg-[#1A1A1A] border border-white/5 hover:border-[#EAB308]/30 rounded-xl p-5 group transition-all">
                <p className="text-white text-sm font-medium mb-1 group-hover:text-[#EAB308] transition-colors">{r.breadcrumb}</p>
                <p className="text-[#555] text-xs">{r.badge}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <div className="h-16 md:h-14" aria-hidden="true" />
    </main>
  );
};

export default LandingPage;
